// =============================================
// Layer 4 — SEO Meta Sanity Audit
// FREE (fetch live WP HTML), ~500ms
// =============================================
// Sprawdza:
//   - Czy <title> w live WP page == seo_title z DB (Yoast wpłynął OK?)
//   - Czy <meta name="description"> == meta_description z DB
//   - Długość: title 30-65, description 120-170 (CTR sweet spot)
//   - og:title / og:description (Yoast auto-copy)
// =============================================

export interface SeoMetaAuditResult {
  status: "ok" | "warning" | "failed";
  live_title: string | null;
  live_description: string | null;
  expected_title: string | null;
  expected_description: string | null;
  title_match: boolean;
  description_match: boolean;
  title_length: number;
  description_length: number;
  og_title_match: boolean | null;
  og_description_match: boolean | null;
  issues: string[];
}

const FETCH_TIMEOUT_MS = 8000;

function extractTag(html: string, tagName: string, attrs: Record<string, string> = {}): string | null {
  // Build regex: <tagName attr1="val1" attr2="val2" ...>content</tagName> OR self-closing
  const attrPatterns = Object.entries(attrs)
    .map(([k, v]) => `${k}=["']${v}["']`)
    .join("[^>]*");
  // <title> jest paired, <meta> jest self-closing
  if (tagName === "title") {
    const m = html.match(/<title\b[^>]*>([\s\S]*?)<\/title>/i);
    return m ? m[1].trim() : null;
  }
  // <meta content="..." name="..."> — content może być przed lub po name
  if (tagName === "meta") {
    const attrEntries = Object.entries(attrs);
    if (attrEntries.length === 0) return null;
    const [keyAttr, keyVal] = attrEntries[0];
    // np. name="description" OR property="og:title"
    const regex = new RegExp(
      `<meta\\b[^>]*\\b${keyAttr}=["']${keyVal}["'][^>]*>`,
      "i",
    );
    const m = html.match(regex);
    if (!m) return null;
    const tag = m[0];
    const contentMatch = tag.match(/content=["']([^"']*)["']/i);
    return contentMatch ? contentMatch[1] : null;
  }
  return null;
}

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ");
}

function looseEquals(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  const normA = decodeHtmlEntities(a).replace(/\s+/g, " ").trim().toLowerCase();
  const normB = decodeHtmlEntities(b).replace(/\s+/g, " ").trim().toLowerCase();
  return normA === normB;
}

/**
 * Audit SEO meta gdy MAMY już Yoast metadata z WP REST (post.meta._yoast_wpseo_*).
 * Bez fetch live HTML, bez CORS. Yoast meta są autorytatywne (= co Yoast renderuje w <head>).
 */
export function auditSeoMetaFromYoast(
  yoastTitle: string | null,
  yoastDescription: string | null,
  expectedSeoTitle: string | null,
  expectedMetaDescription: string | null,
): SeoMetaAuditResult {
  // Audit Layer 4 = sprawdza CZY w DB jest seo_title + meta_description (krok SEO meta
  // pipeline'u wcześniej). Te wartości MY wysyłamy do Yoast meta przy publish (przez
  // wpPost.meta._yoast_wpseo_title w publishQueueService + plugin "Meta API REST Support").
  //
  // Yoast live verify (czy faktycznie WP renderuje te wartości) wymagałby modyfikacji WP
  // (functions.php: register_post_meta z show_in_rest=true) lub plugina który dla READ
  // expose'uje _yoast_wpseo_*. Plugin Meta API REST Support to robi TYLKO przy WRITE.
  //
  // Strategia: sprawdzamy DB → jeśli OK + yoastTitle z REST też present → bonus "verified live".
  // yoastTitle/yoastDescription jako info-only — brak NIE czyni audit warning.

  const issues: string[] = [];
  const titleLen = expectedSeoTitle ? expectedSeoTitle.length : 0;
  const descLen = expectedMetaDescription ? expectedMetaDescription.length : 0;

  if (!expectedSeoTitle) {
    issues.push("Brak seo_title w DB — wygeneruj w kroku 'SEO meta' przed publikacją");
  } else if (titleLen < 30 || titleLen > 70) {
    issues.push(`seo_title length ${titleLen} poza zakresem 30-70 (CTR sweet spot)`);
  }
  if (!expectedMetaDescription) {
    issues.push("Brak meta_description w DB — wygeneruj w kroku 'SEO meta'");
  } else if (descLen < 120 || descLen > 170) {
    issues.push(`meta_description length ${descLen} poza zakresem 120-170`);
  }

  // Yoast live verify (info-only) — gdy plugin eksponuje meta przy READ, możemy potwierdzić.
  let titleMatch = false;
  let descMatch = false;
  if (yoastTitle && expectedSeoTitle) {
    titleMatch = looseEquals(yoastTitle, expectedSeoTitle);
    if (!titleMatch) {
      issues.push(
        `Yoast title w WP różny od DB: WP="${yoastTitle.slice(0, 60)}" vs DB="${expectedSeoTitle.slice(0, 60)}"`,
      );
    }
  } else if (!yoastTitle && expectedSeoTitle) {
    // Info-only — Yoast meta przy READ nie eksponowane przez REST. To NIE jest błąd.
    issues.push(
      "Live Yoast verify: meta przy READ nie eksponowane przez REST API (info only). DB seo_title OK — przy publish trafiło do Yoast.",
    );
  }
  if (yoastDescription && expectedMetaDescription) {
    descMatch = looseEquals(yoastDescription, expectedMetaDescription);
    if (!descMatch) {
      issues.push(
        `Yoast description w WP różny od DB: WP="${yoastDescription.slice(0, 60)}" vs DB="${expectedMetaDescription.slice(0, 60)}"`,
      );
    }
  }

  // Status:
  //   failed = brak DB seo_title lub meta_description (= SEO meta step nie wykonany)
  //   warning = length poza zakresem lub yoast title mismatch
  //   ok = inaczej
  let status: SeoMetaAuditResult["status"] = "ok";
  if (!expectedSeoTitle || !expectedMetaDescription) {
    status = "failed";
  } else if (
    titleLen < 30 || titleLen > 70 ||
    descLen < 120 || descLen > 170 ||
    (yoastTitle && !titleMatch) ||
    (yoastDescription && !descMatch)
  ) {
    status = "warning";
  }

  return {
    status,
    live_title: yoastTitle,
    live_description: yoastDescription,
    expected_title: expectedSeoTitle,
    expected_description: expectedMetaDescription,
    title_match: titleMatch,
    description_match: descMatch,
    title_length: titleLen,
    description_length: descLen,
    og_title_match: null,
    og_description_match: null,
    issues,
  };
}

export async function auditSeoMeta(
  wpPostUrl: string,
  expectedSeoTitle: string | null,
  expectedMetaDescription: string | null,
): Promise<SeoMetaAuditResult> {
  // DISABLED w browser-side: cross-origin fetch do WP site = CORS spam w konsoli +
  // zero wartości audytowej (browser blokuje read response). Pełne wsparcie wymaga
  // Supabase Edge Function 'audit-fetch' (server-side proxy) — osobny ticket.
  //
  // Tymczasem: zero fetch, status "ok" z informacyjną notką. Sprawdzenie Yoast title
  // robić przez Search Console preview albo manual w WP admin.
  void wpPostUrl; // unused dopóki nie ma proxy
  return {
    status: "ok",
    live_title: null,
    live_description: null,
    expected_title: expectedSeoTitle,
    expected_description: expectedMetaDescription,
    title_match: false,
    description_match: false,
    title_length: 0,
    description_length: 0,
    og_title_match: null,
    og_description_match: null,
    issues: [
      "Live SEO meta check pominięty (CORS — wymaga server-side proxy). Sprawdź Yoast preview / Search Console manual.",
    ],
  };

  // Unreachable poniżej — kept dla future Edge Function integration
  // eslint-disable-next-line no-unreachable
  const issues: string[] = [];
  let html = "";
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    const response = await fetch(wpPostUrl, { signal: controller.signal });
    clearTimeout(timer);
    if (!response.ok) {
      return {
        status: "failed",
        live_title: null,
        live_description: null,
        expected_title: expectedSeoTitle,
        expected_description: expectedMetaDescription,
        title_match: false,
        description_match: false,
        title_length: 0,
        description_length: 0,
        og_title_match: null,
        og_description_match: null,
        issues: [`Fetch live URL failed: ${response.status} ${response.statusText}`],
      };
    }
    html = await response.text();
  } catch (err) {
    return {
      status: "warning",
      live_title: null,
      live_description: null,
      expected_title: expectedSeoTitle,
      expected_description: expectedMetaDescription,
      title_match: false,
      description_match: false,
      title_length: 0,
      description_length: 0,
      og_title_match: null,
      og_description_match: null,
      issues: [`Fetch error: ${err instanceof Error ? err.message : "unknown"}`],
    };
  }

  // Wyciągnij wartości
  // Yoast często formatuje <title> jako "{seo_title} - {site name}" — porównanie loose
  const rawTitle = extractTag(html, "title");
  const liveTitle = rawTitle ? decodeHtmlEntities(rawTitle) : null;
  const liveDesc = extractTag(html, "meta", { name: "description" });
  const ogTitle = extractTag(html, "meta", { property: "og:title" });
  const ogDesc = extractTag(html, "meta", { property: "og:description" });

  // Match logic — Yoast może dodawać site suffix do <title>, więc sprawdzamy "starts with seo_title"
  const titleStartsWithExpected =
    expectedSeoTitle && liveTitle
      ? decodeHtmlEntities(liveTitle).toLowerCase().startsWith(expectedSeoTitle.toLowerCase())
      : false;
  const title_match = looseEquals(liveTitle, expectedSeoTitle) || titleStartsWithExpected;
  const description_match = looseEquals(liveDesc, expectedMetaDescription);
  const og_title_match =
    ogTitle && expectedSeoTitle
      ? looseEquals(ogTitle, expectedSeoTitle) ||
        decodeHtmlEntities(ogTitle).toLowerCase().startsWith(expectedSeoTitle.toLowerCase())
      : null;
  const og_description_match =
    ogDesc && expectedMetaDescription ? looseEquals(ogDesc, expectedMetaDescription) : null;

  const title_length = liveTitle ? liveTitle.length : 0;
  const description_length = liveDesc ? liveDesc.length : 0;

  if (expectedSeoTitle && !title_match) {
    issues.push(
      `<title> mismatch: live="${liveTitle?.slice(0, 60)}..." expected="${expectedSeoTitle.slice(0, 60)}..."`,
    );
  }
  if (expectedMetaDescription && !description_match) {
    issues.push(
      `<meta description> mismatch: live="${liveDesc?.slice(0, 60)}..." expected="${expectedMetaDescription.slice(0, 60)}..."`,
    );
  }
  if (liveTitle && (title_length < 30 || title_length > 70)) {
    issues.push(`<title> length ${title_length} poza zakresem 30-70`);
  }
  if (liveDesc && (description_length < 120 || description_length > 170)) {
    issues.push(`<meta description> length ${description_length} poza zakresem 120-170`);
  }
  if (og_title_match === false) issues.push("og:title różny od <title>");
  if (og_description_match === false) issues.push("og:description różny od meta description");

  let status: SeoMetaAuditResult["status"] = "ok";
  if (
    (expectedSeoTitle && !title_match) ||
    (expectedMetaDescription && !description_match)
  ) {
    status = "warning";
  }
  // Length poza zakresem = warning (nie failed — CTR pomocniczy nie blocking)
  // failed tylko przy fetch fail (handled wyżej)

  return {
    status,
    live_title: liveTitle,
    live_description: liveDesc,
    expected_title: expectedSeoTitle,
    expected_description: expectedMetaDescription,
    title_match,
    description_match,
    title_length,
    description_length,
    og_title_match,
    og_description_match,
    issues,
  };
}
